import { useEffect, useState } from "react";
import { Lightbulb, Flame, Save, Trash2, Rocket, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAppMode } from "@/context/AppModeContext";
import { Loader, Bullets, Chips, SectionCard } from "@/components/common";
import { Switch } from "@/components/ui/switch";

function IdeaResult({ d }) {
  const idea = d.best_idea || {};
  return (
    <div className="space-y-4">
      {d.problem_understanding && <SectionCard title="Understanding the problem"><p className="leading-relaxed">{d.problem_understanding}</p></SectionCard>}
      {d.what_already_exists?.length > 0 && <SectionCard title="What already exists"><Bullets items={d.what_already_exists} /></SectionCard>}
      {d.underserved_users?.length > 0 && <SectionCard title="Underserved users"><Bullets items={d.underserved_users} /></SectionCard>}
      <SectionCard title="Strongest concept" icon={Rocket} accent>
        {idea.name && <h3 className="font-head font-extrabold text-2xl">{idea.name}</h3>}
        {idea.one_liner && <p className="text-muted-foreground mt-1">{idea.one_liner}</p>}
        {idea.how_it_works && <p className="mt-3 leading-relaxed">{idea.how_it_works}</p>}
        {idea.why_differentiated?.length > 0 && <div className="mt-3"><p className="font-semibold text-sm mb-2">Appears differentiated by (market research still required):</p><Bullets items={idea.why_differentiated} /></div>}
        {idea.target_users && <p className="mt-3 text-sm"><b>Target users:</b> {idea.target_users}</p>}
        {idea.business_model && <p className="text-sm"><b>Business model:</b> {idea.business_model}</p>}
        {idea.biggest_risk && <p className="text-sm"><b className="text-destructive">Biggest risk:</b> {idea.biggest_risk}</p>}
      </SectionCard>
      {idea.mvp?.length > 0 && <SectionCard title="Build the MVP"><Bullets items={idea.mvp} /></SectionCard>}
      {d.alternative_versions && (
        <SectionCard title="Idea evolution" icon={Layers}>
          <div className="grid sm:grid-cols-2 gap-3">
            {Object.entries(d.alternative_versions).map(([k, v]) => (
              <div key={k} className="rounded-md bg-secondary p-3"><div className="font-head font-bold text-sm capitalize">{k.replace(/_/g, " ")}</div><div className="text-sm text-muted-foreground">{v}</div></div>
            ))}
          </div>
        </SectionCard>
      )}
      {d.validation_plan?.length > 0 && <SectionCard title="How to validate it"><Bullets items={d.validation_plan} /></SectionCard>}
    </div>
  );
}

export default function Ideas() {
  const { mode } = useAppMode();
  const [input, setInput] = useState("");
  const [brutal, setBrutal] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vault, setVault] = useState([]);

  const loadVault = () => api.get("/ideas").then((r) => setVault(r.data.items)).catch(() => {});
  useEffect(() => { loadVault(); }, []);

  const run = async () => {
    if (!input.trim()) { toast.info("Describe a problem or area first."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/features/idea-lab", { input: input.trim(), mode, brutal });
      if (data.error) toast.error("Response unclear — add more detail.");
      else setResult(data);
    } catch { toast.error("Could not generate."); }
    finally { setLoading(false); }
  };

  const save = async () => {
    const name = result?.best_idea?.name || input.slice(0, 40);
    await api.post("/ideas", { title: name, data: { tool: "idea-lab", input, result } });
    toast.success("Saved to Idea Vault");
    loadVault();
  };
  const del = async (id) => { await api.delete(`/ideas/${id}`); loadVault(); };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head font-extrabold text-3xl sm:text-4xl flex items-center gap-3"><Lightbulb className="w-8 h-8 text-primary" />Idea Lab</h1>
        <p className="text-muted-foreground mt-2">Not five generic ideas — one researched, differentiated concept with an MVP and a validation plan.</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-5 sm:p-6">
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={4} data-testid="idea-input"
          placeholder="Describe a problem, industry, audience or constraint. e.g. 'Help small farmers reduce crop waste with almost no budget'"
          className="w-full rounded-md border border-input bg-background px-3 py-3 outline-none focus:ring-2 focus:ring-ring resize-none" />
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
          <label className="flex items-center gap-2.5 text-sm font-semibold">
            <Switch checked={brutal} onCheckedChange={setBrutal} data-testid="brutal-toggle" />
            <span className="inline-flex items-center gap-1.5"><Flame className={`w-4 h-4 ${brutal ? "text-destructive" : "text-muted-foreground"}`} />Brutally Realistic mode</span>
          </label>
          <button onClick={run} disabled={loading} data-testid="idea-run" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-5 py-2.5 hover:brightness-110 disabled:opacity-50 transition">
            <Lightbulb className="w-4 h-4" /> Generate idea
          </button>
        </div>
      </div>

      {loading && <Loader label="Researching, combining fields, removing generic ideas…" />}
      {result && (
        <div className="space-y-4">
          <IdeaResult d={result} />
          <button onClick={save} data-testid="idea-save" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary"><Save className="w-4 h-4" />Save to Idea Vault</button>
        </div>
      )}

      {vault.length > 0 && (
        <div>
          <h2 className="font-head font-bold text-lg mb-3">Your Idea Vault</h2>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="idea-vault">
            {vault.map((v) => (
              <div key={v.id} className="rounded-lg border border-border bg-card p-4 flex items-start justify-between gap-3">
                <div><div className="font-semibold">{v.title}</div><div className="text-xs text-muted-foreground mt-0.5">{new Date(v.created_at).toLocaleDateString()}</div></div>
                <button onClick={() => del(v.id)} data-testid={`idea-del-${v.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
